// Labels for the Launch Agent steps (order = backend ToolRegistry::launchAgent).
export const STEPS = [
  { key: "variation", label: "Reference campaign (variation)" },
  { key: "reference", label: "Read the reference" },
  { key: "parse_prompt", label: "Read the prompt" },
  { key: "preferences", label: "Your settings and what it learned" },
  { key: "fb_assets", label: "Facebook account, page and pixel" },
  { key: "redirect_domain", label: "Redirect domain" },
  { key: "campaign_defaults", label: "Buying strategy, budget and targeting" },
  { key: "blog_site_author", label: "Blog site and author" },
  { key: "categories", label: "Categories" },
  { key: "keywords", label: "Term and keywords" },
  { key: "campaign_name", label: "Campaign name" },
  { key: "ad_copy", label: "Ad copy" },
  { key: "ad_creatives", label: "Ad images" },
  { key: "article", label: "Article" },
  { key: "qa_check", label: "Quality checks" },
];

export const RUN_STATUS = {
  queued: { label: "Queued", tone: "neutral" },
  running: { label: "Running", tone: "info" },
  awaiting_approval: { label: "Ready for review", tone: "good" },
  blocked: { label: "Blocked by checks", tone: "warn" },
  failed: { label: "Failed", tone: "bad" },
  rejected: { label: "Rejected", tone: "neutral" },
  approved: { label: "Approved", tone: "good" },
  launching: { label: "Launching", tone: "info" },
  launched: { label: "Launched", tone: "good" },
  launch_failed: { label: "Launch stopped", tone: "bad" },
  stopped: { label: "Stopped", tone: "neutral" },
};

export const SOURCE_LABELS = {
  learned: "Learned from edits",
  prompt: "Your prompt",
  preference: "Prompt / past edits",
  human: "Reviewer",
  history: "Past data",
  "history+llm": "Past data + AI",
  llm: "AI",
  config: "Config",
  convention: "Naming rule",
  fallback: "Default",
  user: "You",
  rules: "Rules",
};

export const DECISION_LABELS = {
  edit: "Edited plan",
  edit_image: "Changed ad image",
  approve: "Approved",
  reopen: "Reopened for editing",
  topic: "Topic",
  niche: "Niche",
  campaign_prefix: "Campaign prefix",
  ad_account: "Ad account",
  fb_page: "FB page",
  pixel: "Pixel",
  redirect_domain: "Redirect domain",
  buying_strategy: "Buying strategy",
  daily_budget_usd: "Daily budget (USD)",
  audience: "Audience",
  conversion_event: "Conversion event",
  blog_site: "Blog site",
  blog_author: "Author",
  blog_category: "Blog category",
  campaign_category: "Campaign category",
  term_name: "Term name",
  keywords: "Keywords",
  kwb: "KWB",
  campaign_name: "Campaign name",
  ad_copy: "Ad copy",
  ad_creatives: "Ad images",
  article: "Article",
  article_title: "Article title",
  qa: "Quality checks",
};

// Image layouts (backend CreativeRenderer::TEMPLATES).
export const CREATIVE_STYLES = [
  { value: "auto", label: "Mix all layouts" },
  { value: "banner", label: "Banner — photo + teal band + red CTA" },
  { value: "question", label: "Question — photo + cyan panel" },
  { value: "poster", label: "Poster — big headline + badges" },
  { value: "side", label: "Guide — checklist + photo on the right" },
];

// Same standard as the Create Blog Post generator (backend agent_v2.article).
export const MIN_ARTICLE_WORDS = 1400;
export const MIN_ARTICLE_LINKS = 3;

// The standard way to write a prompt (same labels the server reads — PromptFormat.php). "Insert template" puts it in the box.
// Lines left blank are ignored.
const TEMPLATE_LINES = ["Topic", "Ad account", "Page", "Blog site", "Redirect domain", "Pixel", "Conversion event", "Audience", "Keywords", "Campaign name", "Notes"];
export const PROMPT_TEMPLATE = TEMPLATE_LINES.map((l) => (l === "Conversion event" ? `${l}: PURCHASE` : `${l}: `)).join("\n");
// A variation: every line blank — anything left blank is kept from the source campaign (its page, pixel, conversion event…).
export const VARIATION_TEMPLATE = TEMPLATE_LINES.map((l) => `${l}: `).join("\n");
// "How to fill" next to Insert template: what each line takes, an example, and what happens when it is left blank.
export const TEMPLATE_HELP = [
  { label: "Topic", what: "What the campaign is about. In a variation: a NEW topic made in the source campaign's style.", example: "Aesthetic treatments for women 40+", blank: "Read from the rest of your prompt", blankVariation: "The source campaign's topic" },
  { label: "Ad account", what: "Business + account code, as in the ad account's name.", example: "V3-TASH", blank: "The account that earned most on this topic", blankVariation: "The source campaign's" },
  { label: "Page", what: "The Facebook page name.", example: "Thrive and Shine", blank: "The page mapped to the ad account", blankVariation: "The source campaign's" },
  { label: "Blog site", what: "The site the article is published on (domain only).", example: "learn.the-health-guide.com", blank: "The site that did best on this topic", blankVariation: "The site that did best on this topic" },
  { label: "Redirect domain", what: "One of our redirect domains; a subdomain in front is kept.", example: "search2.amazing-find.com", blank: "The domain mapped to the page", blankVariation: "The source campaign's" },
  { label: "Pixel", what: "The pixel's name in Ads Manager; its ID in brackets is fine.", example: "NM-Senior-Prime (215040918777447)", blank: "The ad account's standard Serp pixel", blankVariation: "The source campaign's" },
  { label: "Conversion event", what: "PURCHASE or another event from the list.", example: "PURCHASE", blank: "PURCHASE", blankVariation: "The source campaign's" },
  { label: "Audience", what: "The audience tag or its name.", example: "(d-SP) or Senior-Prime", blank: "Chosen for the topic", blankVariation: "The source campaign's" },
  { label: "Keywords", what: "The feed terms, separated by commas (or one per line under “Keywords:”).", example: "dermal fillers near me, juvederm near me", blank: "Our best performers for the topic", blankVariation: "The source's best keywords — or, with a new Topic, picked for that topic" },
  { label: "Campaign name", what: "The start of the name: Prefix(Category). The rest is added: (audience)(buying strategy)(r-nm)-Business-Account-FB-You.", example: "HG(ExmpCategory)", blank: "Built by the naming convention", blankVariation: "Built by the naming convention (write it to keep the source's prefix)" },
  { label: "Notes", what: "Anything else for the article and the ads: angle, tone, what to avoid, image style.", example: "Same structure as the reference article; warmer tone; no prices", blank: "—", blankVariation: "—" },
];

// Lines that are not in the template but are read too — on their own line or inside Notes (PromptFormat::extras).
export const TEMPLATE_EXTRAS = [
  { label: "Source", what: "A variation: the campaign (ad set) to copy — its full name or ad set ID. “Source adset:” works too.", example: "Cruises(VikingRiver2)(ROAS)(d-SSP)(r-nm)-NM-IT-FB-CS" },
  { label: "ROAS goal", what: "Facebook bid strategy “ROAS goal” with this number (else Highest value).", example: "1.10" },
  { label: "RAC", what: "The campaign's RAC instead of the article title. One per campaign: with several, the first is used.", example: "A Guide To Viking River Cruises" },
  { label: "Winning article", what: "The article the new one is written from (instead of the source campaign's own).", example: "https://learn.updateblast.com/editorial/…" },
  { label: "Ads", what: "A variation: the source's ads (Facebook ad IDs) the new images follow, instead of its best by ROAS.", example: "120253387185900577, 120253387185880577" },
];

/** The prompt with the template added under it (once). */
export const withTemplate = (prompt, variation) => {
  const raw = String(prompt || "");
  if (/^\s*ad account\s*:/im.test(raw) && /^\s*keywords\s*:/im.test(raw)) return raw; // already there: unchanged
  const p = raw.replace(/\s+$/, "");
  const t = variation ? VARIATION_TEMPLATE : PROMPT_TEMPLATE;
  return p ? `${p}\n\n${t}` : t;
};

// "… with 3 ads", "Ads: 4", "three ads" in the prompt — the server uses it over the Ads field (same rules as PromptAds.php).
const AD_WORDS = { one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10 };
/** "budget- $20", "Budget: 25", "$20/day", "daily budget $30" in the prompt → 20 (same rule as the server's prompt settings). */
export const budgetInPrompt = (prompt) => {
  const m = /budget\D{0,12}\$?\s?(\d{1,4}(?:\.\d{1,2})?)|\$\s?(\d{1,4}(?:\.\d{1,2})?)\s*(?:\/\s*day|per\s+day|a\s+day|daily)/i.exec(String(prompt || ""));
  if (!m) return null;
  const n = Number(m[1] || m[2]);
  return n > 0 ? n : null;
};

export const adsInPrompt = (prompt) => {
  const p = String(prompt || "").toLowerCase();
  const num = `(\\d{1,2}|${Object.keys(AD_WORDS).join("|")})`;
  const m =
    p.match(new RegExp(`\\b(?:number of ads|ads?)\\s*[:=]\\s*${num}\\b`)) ||
    p.match(new RegExp(`(?<!top )(?<!best )(?<!winning )(?<!last )(?<!first )\\b${num}\\s+(?:new\\s+|different\\s+|fresh\\s+)?(?:facebook\\s+|fb\\s+)?(?:ads|ad creatives|creatives)\\b(?!\\s*(?:sets?\\b|by\\b))`));
  if (!m) return null;
  const n = /^\d+$/.test(m[1]) ? Number(m[1]) : AD_WORDS[m[1]];
  return n > 0 ? n : null;
};

// Top bar bell ↔ Agent Studio (window events, so the bell works on every screen without a shared store).
export const PLAN_STARTED_EVENT = "agent-studio:plan-started"; // a plan was started or retried: the bell checks soon, then every 20 s
export const OPEN_PLAN_EVENT = "agent-studio:open-plan"; // the bell asks Agent Studio (already open) to show a plan
export const planStarted = () => {
  try {
    window.dispatchEvent(new Event(PLAN_STARTED_EVENT));
  } catch (e) {
    // old browser: the bell still checks every 2 minutes
  }
};

// The article vs the feed terms now on the plan: which terms were added / removed since it was written.
// null when unknown (an older plan that did not record its keywords).
export const articleKeywordChange = (blog) => {
  const used = blog?.article?.keywords_used;
  if (!Array.isArray(used)) return null;
  const norm = (k) => String(k).trim().toLowerCase();
  const now = (blog?.term?.keywords || []).map(norm);
  const was = used.map(norm);
  const added = now.filter((k) => !was.includes(k));
  const removed = was.filter((k) => !now.includes(k));
  return { added, removed, changed: added.length + removed.length > 0 };
};

export const LIMITS = { maxAds: 10, maxBudget: 50, defaultBudget: 30, headline: 40, primary: 125, description: 30 };

// Terminal = nothing more will happen without a user action.
export const TERMINAL = ["awaiting_approval", "blocked", "failed", "rejected", "approved", "launched", "launch_failed", "stopped"];

// Image links (ad images, featured image) are files on the API server. Plans built by the queue worker
// can carry "http://localhost/…" links, so every image link uses BACKEND_URL without its last "api/"
// (e.g. https://api.example.com/api/ → https://api.example.com/) plus the file path.
export const fileBase = (backendUrl) => (backendUrl || "").replace(/\/?api\/?$/i, "").replace(/\/+$/, "");

export const fileUrl = (url, backendUrl) => {
  const base = fileBase(backendUrl);
  if (typeof url !== "string" || !base) return url;
  const m = url.match(/^https?:\/\/([^/]+)(\/.*)$/i);
  if (!m) return url;
  const ours = /\/ai-images\//i.test(m[2]) || /^(localhost|127\.0\.0\.1|\[::1\])(:\d+)?$/i.test(m[1]);
  return ours ? base + m[2] : url;
};

// The same for every link in a plan (ads, creatives, their originals, blog).
export const withFileUrls = (value, backendUrl) => {
  if (!fileBase(backendUrl)) return value;
  if (typeof value === "string") return /^https?:\/\//i.test(value) ? fileUrl(value, backendUrl) : value;
  if (Array.isArray(value)) return value.map((v) => withFileUrls(v, backendUrl));
  if (value && typeof value === "object") return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, withFileUrls(v, backendUrl)]));
  return value;
};
